// Qualify bare column references that a new JOIN would make ambiguous.
//
// `SELECT id, name FROM customers` + `JOIN orders o ON …` fails at run time
// when `orders` also has `id` ("ambiguous column name"). The `add-join` op can
// therefore carry `qualifyColumns` — names the caller (who has the schema)
// found in BOTH the joined table and one relation already in the query, each
// with that relation's exposed name — and this planner prefixes every bare
// reference to them with `<qualifier>.` in the same edit as the join.
//
// Scope of the scan: the statement's own SELECT items (expression part only,
// never the alias), WHERE / HAVING fragments, GROUP BY items and ORDER BY
// items. It skips strings, comments, nested `(SELECT …)` / `(WITH …)`
// subqueries (their names belong to another scope), names after or before a
// `.`, function names, names after `AS`, ORDER BY references to a SELECT
// alias, and date-part keywords used as arguments (`EXTRACT(YEAR FROM …)`,
// `DATEADD(day, …)`, `INTERVAL '1' DAY`). Qualifying a bare name in a query
// that was valid before the join is always correct: every bare reference
// already resolved to the one relation the caller names as its qualifier.

import type { PlannedByteEdit, QualifyColumn } from '../types';

import { skipContextFor, trySkipNonCode, type SkipContext } from '../classify/tokenSkip';

import type { EditCtx } from './applyStructuralEdit';
import { insertEdit } from './applyStructuralEdit.shared';

const DATE_PART_FUNCTIONS: ReadonlySet<string> = new Set([
  'DATEADD', 'DATEDIFF', 'DATEDIFF_BIG', 'DATEPART', 'DATENAME', 'DATETRUNC', 'DATE_BUCKET',
  'TIMESTAMPADD', 'TIMESTAMPDIFF',
]);

type Token = { readonly start: number; readonly end: number; readonly text: string; readonly quoted: boolean };

export function isWordStart(c: string): boolean {
  return /[A-Za-z_\u0080-￿]/.test(c);
}

export function readWord(source: string, i: number, end: number): number {
  let j = i;
  while (j < end && /[\w$#\u0080-￿]/.test(source[j])) j++;
  return j;
}

export function skipWs(source: string, i: number, end: number, skip: SkipContext): number {
  let j = i;
  for (;;) {
    while (j < end && /\s/.test(source[j])) j++;
    if (j >= end) return j;
    const c = source[j];
    const isComment =
      (c === '-' && source[j + 1] === '-') || (c === '/' && source[j + 1] === '*') || (skip.supportsHashComment && c === '#');
    if (!isComment) return j;
    j = trySkipNonCode(source, j, end, skip);
  }
}

/** Index just past the `)` matching the `(` at `open`, skipping strings / comments / quoted names. */
export function matchingParenEnd(source: string, open: number, end: number, skip: SkipContext): number {
  let depth = 0;
  let i = open;
  while (i < end) {
    const skipped = trySkipNonCode(source, i, end, skip);
    if (skipped !== -1) {
      i = skipped;
      continue;
    }
    if (source[i] === '(') depth++;
    else if (source[i] === ')') {
      depth--;
      if (depth === 0) return i + 1;
    }
    i++;
  }
  return end;
}

export function isQuoteOpen(c: string, skip: SkipContext): boolean {
  return c === '"' || (skip.supportsBracket && c === '[') || (skip.supportsBacktick && c === '`');
}

/**
 * Offsets of bare references to `names` (folded lower-case → qualifier) in
 * `[start, end)`, per the rules in the file header.
 */
export function findBareColumnRefs(
  source: string,
  start: number,
  end: number,
  names: ReadonlyMap<string, string>,
  skip: SkipContext,
  excluded: ReadonlySet<string> = new Set(),
): { offset: number; qualifier: string }[] {
  const out: { offset: number; qualifier: string }[] = [];
  let prevSignificant = '';
  let prevWord = '';
  let wordBeforeParen = '';
  let i = start;

  const consider = (tok: Token): void => {
    const next = skipWs(source, tok.end, end, skip);
    const nextChar = source[next] ?? '';
    const nextWordEnd = isWordStart(nextChar) ? readWord(source, next, end) : next;
    const nextWord = source.slice(next, nextWordEnd).toUpperCase();
    const folded = tok.text.toLowerCase();
    const qualifier = names.get(folded);
    const isDatePartArg =
      prevSignificant === '(' &&
      (nextWord === 'FROM' || (nextChar === ',' && DATE_PART_FUNCTIONS.has(wordBeforeParen)));
    const afterLiteral = prevSignificant === 'literal';
    if (
      qualifier !== undefined &&
      !excluded.has(folded) &&
      prevSignificant !== '.' &&
      nextChar !== '.' &&
      nextChar !== '(' &&
      prevWord !== 'AS' &&
      !isDatePartArg &&
      !afterLiteral
    ) {
      out.push({ offset: tok.start, qualifier });
    }
    prevSignificant = 'word';
    prevWord = tok.quoted ? '' : tok.text.toUpperCase();
  };

  while (i < end) {
    const c = source[i];
    if (/\s/.test(c)) {
      i++;
      continue;
    }
    if (isQuoteOpen(c, skip)) {
      const j = trySkipNonCode(source, i, end, skip);
      const inner = source.slice(i + 1, Math.max(i + 1, j - 1));
      consider({ start: i, end: j, text: c === '"' ? inner.replace(/""/g, '"') : inner, quoted: true });
      i = j;
      continue;
    }
    const skipped = trySkipNonCode(source, i, end, skip);
    if (skipped !== -1) {
      // A string literal (or a comment, which leaves the previous state alone).
      if (c === "'" || c === '$') {
        prevSignificant = 'literal';
        prevWord = '';
      }
      i = skipped;
      continue;
    }
    if (c === '(') {
      const k = skipWs(source, i + 1, end, skip);
      const firstWord = source.slice(k, readWord(source, k, end)).toUpperCase();
      if (firstWord === 'SELECT' || firstWord === 'WITH') {
        i = matchingParenEnd(source, i, end, skip);
        prevSignificant = ')';
        prevWord = '';
        continue;
      }
      wordBeforeParen = prevWord;
      prevSignificant = '(';
      prevWord = '';
      i++;
      continue;
    }
    if (isWordStart(c)) {
      const j = readWord(source, i, end);
      consider({ start: i, end: j, text: source.slice(i, j), quoted: false });
      i = j;
      continue;
    }
    prevSignificant = c;
    prevWord = '';
    i++;
  }
  return out;
}

/** Planned `<qualifier>.` inserts for every bare reference to `columns` in the statement. */
export function planQualifyColumns(ctx: EditCtx, columns: readonly QualifyColumn[]): PlannedByteEdit[] {
  if (columns.length === 0) return [];
  const names = new Map(columns.map((c) => [c.name.toLowerCase(), c.qualifier]));
  const skip = skipContextFor(ctx.options);
  const { source, structure } = ctx;
  const refs: { offset: number; qualifier: string }[] = [];

  for (const item of structure.selectItems ?? []) {
    // Expression part only — the alias (`name AS n` / `name n`) is never a reference.
    const exprStart = source.indexOf(item.expressionText, item.range.startOffset);
    const from = exprStart >= 0 && exprStart < item.range.endOffset ? exprStart : item.range.startOffset;
    const to = exprStart >= 0 ? Math.min(item.range.endOffset, from + item.expressionText.length) : item.range.endOffset;
    refs.push(...findBareColumnRefs(source, from, to, names, skip));
  }
  for (const fragment of [...(structure.where ?? []), ...(structure.having ?? [])]) {
    refs.push(...findBareColumnRefs(source, fragment.range.startOffset, fragment.range.endOffset, names, skip));
  }
  for (const ref of structure.groupBy ?? []) {
    refs.push(...findBareColumnRefs(source, ref.range.startOffset, ref.range.endOffset, names, skip));
  }
  // ORDER BY may name a SELECT alias — that is not a column reference.
  const aliases = new Set((structure.selectItems ?? []).flatMap((s) => (s.alias ? [s.alias.toLowerCase()] : [])));
  for (const item of structure.orderBy ?? []) {
    refs.push(...findBareColumnRefs(source, item.range.startOffset, item.range.endOffset, names, skip, aliases));
  }

  const seen = new Set<number>();
  return refs
    .filter((r) => (seen.has(r.offset) ? false : (seen.add(r.offset), true)))
    .map((r) => insertEdit(ctx, r.offset, `${r.qualifier}.`, 'add-join:qualify-column'));
}
